/**
 * @file LandingPage.tsx
 * @description Public landing page. Briefly describes the portal and links
 *   to Login. Self-registration is not exposed — accounts are created via
 *   admin invitation.
 * @module Pages
 * @security No user input here; safe to render as the unauthenticated
 *   entry point. CSP-friendly (no inline scripts, no external assets).
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '../components/common/BrandMark';
import { LanguageToggle } from '../components/common/LanguageToggle';
import { PublicFooter } from '../components/common/PublicFooter';

export default function LandingPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-5">
        <BrandMark label={t('landing.brand')} />
        <LanguageToggle />
      </header>
      <section className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl text-center space-y-8 animate-fade-up">
          {/* Single brand-gradient phrase — the design system's one hero
              accent per page. */}
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
            <span className="text-brand-gradient">{t('landing.title')}</span>
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">{t('landing.subtitle')}</p>
          <div className="flex gap-3 justify-center pt-2">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-gradient text-brand-fg text-sm font-semibold shadow-card hover:shadow-lift active:scale-[0.98] transition-[box-shadow,transform] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              {t('landing.login_cta')}
            </Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
